import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Grid, Slider, Typography } from '@mui/material'
import { useAtom, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router'
import useFetchEntitiesWithCache from '../../../hooks/useFetchEntitiesWithCache'
import { useFilterIssues } from '../../issues/utils'
import {
    issueActiveTabAtom,
    issueFiltersAtom,
    IssueFilterType,
    issueGridCurrentPageAtom,
} from '../../../atoms/issueAtom'
import { useLogUserEvent } from '../../../hooks/useLogUserEvent'
import UserEventApi from '../../../api/UserEventApi'
import DropdownSelect from '../../components/DropdownSelect'
import {
    ADVISOR_TYPE_OPTIONS,
    AdvisorTypeKey,
    buildAdvisorStatistics,
    getSegmentKeysInOrder,
    prepareChartDisplay,
    SEGMENTATION_OPTIONS,
    SegmentationType,
} from './utils'
import { AdvisorsChart } from './AdvisorsChart'

const MIN_ADVISORS = 1
const MAX_ADVISORS = 20
const DEFAULT_ADVISORS = 10

export const AdvisorsView = () => {
    useLogUserEvent(UserEventApi.EVENT_TYPES.SHOW_STATS_ADVISORS)
    const { isLoading, result: fetchedIssues } = useFetchEntitiesWithCache('issue')
    const [filters] = useAtom(issueFiltersAtom)
    const filteredIssues = useFilterIssues(fetchedIssues || [], filters)

    const [selectedAdvisorTypes, setSelectedAdvisorTypes] = useState<AdvisorTypeKey[]>([])
    const [segmentation, setSegmentation] = useState<SegmentationType>('none')
    const [maxAdvisors, setMaxAdvisors] = useState(DEFAULT_ADVISORS)

    const advisorRows = useMemo(() => {
        if (selectedAdvisorTypes.length === 0) return []

        return buildAdvisorStatistics(filteredIssues, selectedAdvisorTypes, segmentation)
    }, [filteredIssues, selectedAdvisorTypes, segmentation])

    const segmentKeys = useMemo(() => getSegmentKeysInOrder(advisorRows, segmentation), [advisorRows, segmentation])

    const chartRows = useMemo(() => prepareChartDisplay(advisorRows, maxAdvisors), [advisorRows, maxAdvisors])
    const setFilters = useSetAtom(issueFiltersAtom)
    const setActiveTab = useSetAtom(issueActiveTabAtom)
    const setIssueGridCurrentPage = useSetAtom(issueGridCurrentPageAtom)
    const navigate = useNavigate()
    const [oldFilter, setOldFilter] = useState<IssueFilterType | null>(null)

    useEffect(() => {
        setFilters((prevFilters) => {
            if (!oldFilter) setOldFilter(prevFilters)

            return {
                ...prevFilters,
                issueTypes: {
                    value: prevFilters.issueTypes.value.filter((type) => type !== 'Non-cash issue'),
                    disabledItems: [],
                },
            }
        })

        return () => {
            if (oldFilter) setFilters((filters) => ({ ...filters, issueTypes: { ...oldFilter.issueTypes } }))
        }
    }, [setFilters, setOldFilter, oldFilter])

    const handleAdvisorClick = useCallback(
        (advisorId: string) => {
            if (!advisorId) return

            setFilters((prevFilters) => {
                const filters = { ...prevFilters }

                if (selectedAdvisorTypes.includes('financial_advisor')) {
                    filters.financialAdvisors = { ...filters.financialAdvisors, value: [advisorId] }
                }
                if (selectedAdvisorTypes.includes('legal_advisor')) {
                    filters.legalAdvisors = { ...filters.legalAdvisors, value: [advisorId] }
                }

                return filters
            })
            setActiveTab('current')
            setIssueGridCurrentPage(0)
            void navigate('/issues')
        },
        [navigate, selectedAdvisorTypes, setActiveTab, setFilters, setIssueGridCurrentPage],
    )

    const visibleSegmentKeys = useMemo(() => {
        if (segmentation === 'none') return ['total']

        return segmentKeys.filter((key) => chartRows.some((row) => (row.segmentCounts[key] ?? 0) > 0))
    }, [chartRows, segmentKeys, segmentation])

    return (
        <Grid container spacing={6}>
            <Grid size={{ xs: 12, md: 4 }}>
                <DropdownSelect
                    label='Typ av rådgivare'
                    placeholder='Välj rådgivartyp'
                    items={ADVISOR_TYPE_OPTIONS}
                    selectedItems={selectedAdvisorTypes}
                    onChange={(items) => setSelectedAdvisorTypes(items as AdvisorTypeKey[])}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <DropdownSelect
                    label='Segmentering'
                    placeholder='Välj segmentering'
                    items={SEGMENTATION_OPTIONS}
                    selectedItems={[segmentation]}
                    onChange={(items) => setSegmentation((items[0] as SegmentationType) ?? 'none')}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant='body2' gutterBottom>
                    Antal rådgivare: {maxAdvisors}
                </Typography>
                <Slider
                    value={maxAdvisors}
                    min={MIN_ADVISORS}
                    max={MAX_ADVISORS}
                    step={1}
                    marks
                    valueLabelDisplay='auto'
                    onChange={(_, value) => setMaxAdvisors(value)}
                    disabled={selectedAdvisorTypes.length === 0}
                />
            </Grid>
            <Grid size={12}>
                {selectedAdvisorTypes.length === 0 ? (
                    <Typography
                        variant='body1'
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        Välj minst en typ av rådgivare för att visa statistik.
                    </Typography>
                ) : isLoading ? (
                    <Typography
                        variant='body1'
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        Laddar...
                    </Typography>
                ) : chartRows.length === 0 ? (
                    <Typography
                        variant='body1'
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        Inga emissioner matchar filtren för valda rådgivartyper.
                    </Typography>
                ) : (
                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                        <AdvisorsChart
                            chartRows={chartRows}
                            segmentKeys={visibleSegmentKeys.length > 0 ? visibleSegmentKeys : segmentKeys}
                            segmentation={segmentation}
                            onAdvisorClick={handleAdvisorClick}
                        />
                    </Box>
                )}
            </Grid>
        </Grid>
    )
}
